
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import emotionDetectionService from "@/utils/emotionDetectionService";

// Update emoji imports to use absolute URLs that work in production
const angryEmoji = "/emojis/angry.png";
const disgustEmoji = "/emojis/disgust.png";
const fearEmoji = "/emojis/Fear.png";
const happyEmoji = "/emojis/Happy.png";
const neutralEmoji = "/emojis/Neutral.png";
const sadEmoji = "/emojis/Sad.png";
const surpriseEmoji = "/emojis/Surprise.png";
const ecstaticEmoji = "/emojis/Ecstatic.png";
const justOkEmoji = "/emojis/Just-Ok.png";
const ohMyGodEmoji = "/emojis/OhMyGod.png";
const contemptEmoji = "/emojis/contempt.png";
const anxiousEmoji = "/emojis/Anxious.png";
const startledEmoji = "/emojis/Startled.png";
const flatEmoji = "/emojis/Flat.png";
const boredEmoji = "/emojis/bored.png";

interface EmotionDetectorProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  onEmotionDetected: (emotion: string, emojiUrl: string, percentageText: string | null) => void;
  onEmotionFrequencyUpdate?: (emotionFrequencies: string) => void;
}

// Map emotions to emoji files
export const emotionEmojiMap: Record<string, string> = {
  angry: angryEmoji,
  disgust: disgustEmoji,
  fear: fearEmoji,
  happy: happyEmoji,
  neutral: neutralEmoji,
  sad: sadEmoji,
  surprised: surpriseEmoji,
  ecstatic: ecstaticEmoji,
  'just-ok': justOkEmoji,
  'ohmygod': ohMyGodEmoji,
  'contempt': contemptEmoji,
  'bored': boredEmoji,
  'anxious': anxiousEmoji,
  'startled': startledEmoji,
  'flat': flatEmoji,
  // Ensure case-insensitive lookups work
  'Anxious': anxiousEmoji,
  'Startled': startledEmoji,
  'Flat': flatEmoji,
  'Ecstatic': ecstaticEmoji,
  'Just-Ok': justOkEmoji,
  'OhMyGod': ohMyGodEmoji,
  'Contempt': contemptEmoji,
  'Bored': boredEmoji,
  // Fallback for any other emotions
  default: neutralEmoji
};

export const getEmojiForEmotion = (emotion: string): string => {
  // Ensure emotion is valid
  const normalizedEmotion = emotion?.toLowerCase().trim() || 'neutral';
  return emotionEmojiMap[normalizedEmotion] || emotionEmojiMap.default;
};

const EmotionDetector = ({ videoRef, onEmotionDetected, onEmotionFrequencyUpdate }: EmotionDetectorProps) => {
  const detectionIntervalRef = useRef<number | null>(null);
  const [isDetecting, setIsDetecting] = useState(false);
  // Track emotion frequencies during recording
  const emotionFrequenciesRef = useRef<Record<string, number>>({});
  const detectionActive = useRef<boolean>(true);
  const lastDetectedEmotionsRef = useRef<string[]>([]);
  const detectionQueueRef = useRef<{emotion: string, emojiUrl: string, percentageText: string | null}[]>([]);
  const modelLoadAttemptsRef = useRef(0);
  const maxModelLoadAttempts = 3;
  
  // Improved emotion formatting
  const formatEmotionText = (emotion: string, confidence: number | null): string => {
    const formattedEmotion = emotion.charAt(0).toUpperCase() + emotion.slice(1);
    if (confidence === null) return formattedEmotion;
    return `${Math.round(confidence)}%`;
  };

  // Preload emoji images to prevent display issues
  useEffect(() => {
    const preloadEmojis = () => {
      Object.values(emotionEmojiMap).forEach(url => {
        if (url) {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.src = url;
        }
      });
      console.log("Emoji images preloaded");
    };
    
    preloadEmojis();
  }, []);

  // Improved emotion processing with fallbacks - ALWAYS show an emotion
  const processEmotion = (emotion: string, emojiUrl: string, percentageText: string | null) => {
    try {
      // Sanitize emotion
      const sanitizedEmotion = emotion.includes('Infinity') || emotion.includes('NaN') || !emotion
        ? 'neutral' 
        : emotion;
      
      // Ensure we have a valid emoji URL
      const sanitizedEmojiUrl = emojiUrl && typeof emojiUrl === 'string' && emojiUrl.length > 0
        ? emojiUrl
        : emotionEmojiMap.default;
      
      // Ensure we have valid percentage text
      const sanitizedPercentageText = percentageText?.includes('Infinity') || percentageText?.includes('NaN') || !percentageText
        ? formatEmotionText(sanitizedEmotion, 50)
        : percentageText;
      
      // Update the lastDetectedEmotions queue
      if (lastDetectedEmotionsRef.current.length >= 3) {
        lastDetectedEmotionsRef.current.pop();
      }
      lastDetectedEmotionsRef.current.unshift(sanitizedEmotion);
      
      // Update parent component with properly formatted data
      onEmotionDetected(sanitizedEmotion, sanitizedEmojiUrl, sanitizedPercentageText);
    } catch (error) {
      console.error("Error processing emotion:", error);
      const fallbackEmotion = 'neutral';
      const fallbackEmoji = emotionEmojiMap.default;
      const fallbackPercentage = formatEmotionText(fallbackEmotion, 50);
      onEmotionDetected(fallbackEmotion, fallbackEmoji, fallbackPercentage);
    }
  };

  useEffect(() => {
    const loadModelsAndDetect = async () => {
      try {
        // Force immediate model loading with retries
        let modelsLoaded = false;
        
        // First attempt - eager loading with shorter timeout
        try {
          console.log("Initial attempt to load emotion detection models...");
          modelsLoaded = await emotionDetectionService.loadModels();
          console.log("Face detection models loaded:", modelsLoaded);
        } catch (loadError) {
          console.error("Initial error loading models:", loadError);
        }
        
        // Second attempt if first failed - try with different settings
        if (!modelsLoaded && modelLoadAttemptsRef.current < maxModelLoadAttempts) {
          modelLoadAttemptsRef.current++;
          console.log(`Retrying model load, attempt ${modelLoadAttemptsRef.current} of ${maxModelLoadAttempts}`);
          
          try {
            // Wait a moment before retry
            await new Promise(resolve => setTimeout(resolve, 1500));
            modelsLoaded = await emotionDetectionService.loadModels();
            console.log("Face detection models loaded on retry:", modelsLoaded);
          } catch (retryError) {
            console.error(`Retry ${modelLoadAttemptsRef.current} failed:`, retryError);
          }
        }
        
        if (modelsLoaded) {
          toast.success("Emotion detection activated");
          setIsDetecting(true);
          // Start real detection
          startDetection(true);
        } else {
          // Fallback to simulation after failed attempts
          console.warn("Using simulated emotion detection after failed model loads");
          toast.warning("Using simulated emotion detection - models failed to load");
          setIsDetecting(true);
          // Start fallback detection
          startDetection(false);
        }

        // Always ensure we have some emotion display even if detection fails
        onEmotionDetected('neutral', neutralEmoji, '50%');
      } catch (error) {
        console.error("Error in emotion detection setup:", error);
        toast.error("Could not load emotion detection models. Using simulation instead.");
        setIsDetecting(true);
        // Start fallback detection
        startDetection(false);
        
        // Always ensure we have some emotion display
        onEmotionDetected('neutral', neutralEmoji, '50%');
      }
    };

    loadModelsAndDetect();

    return () => {
      // Clean up detection loop on unmount
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
      }
      detectionActive.current = false;
    };
  }, []);

  const startDetection = (useRealDetection: boolean) => {
    if (!videoRef.current) {
      console.error("Video reference is not available for emotion detection");
      
      // Always provide a fallback emotion while waiting for video
      onEmotionDetected('neutral', neutralEmoji, '50%');
      
      setTimeout(() => startDetection(useRealDetection), 500); // Try again soon
      return;
    }
    
    // Make sure video is playing before detecting
    if (videoRef.current.paused || videoRef.current.ended) {
      console.log("Video is paused or ended, waiting...");
      
      // Always provide a fallback emotion while waiting for video
      onEmotionDetected('neutral', neutralEmoji, '50%');
      
      setTimeout(() => startDetection(useRealDetection), 500);
      return;
    }

    console.log(`Starting ${useRealDetection ? "real" : "simulated"} emotion detection with video:`, 
      videoRef.current.videoWidth, "×", videoRef.current.videoHeight);

    // Clear any existing interval
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
    }

    if (useRealDetection) {
      // Real emotion detection with model - increased frequency for smoother updates
      detectionIntervalRef.current = window.setInterval(async () => {
        if (!detectionActive.current) return;

        if (!videoRef.current) {
          console.warn("Video ref lost during emotion detection");
          // Provide fallback
          processEmotion('neutral', emotionEmojiMap.neutral, formatEmotionText('neutral', 50));
          return;
        }

        try {
          // Detect emotion from video frame
          const result = await emotionDetectionService.detectEmotion(videoRef.current);
          
          if (result) {
            const { adjustedEmotion, emojiUrl, confidence, percentageText } = result;
            
            // Skip processing if we detect invalid data
            if (adjustedEmotion.includes('Infinity') || adjustedEmotion.includes('NaN')) {
              processEmotion('neutral', emotionEmojiMap.neutral, formatEmotionText('neutral', 50));
              return;
            }
            
            // Track emotion frequencies
            emotionFrequenciesRef.current[adjustedEmotion] = 
              (emotionFrequenciesRef.current[adjustedEmotion] || 0) + 1;
              
            // Notify parent about updated emotion frequencies with stringified data
            if (onEmotionFrequencyUpdate) {
              onEmotionFrequencyUpdate(JSON.stringify(emotionFrequenciesRef.current));
            }
            
            // Make sure we have proper emotion percentage text
            const properPercentageText = percentageText || formatEmotionText(adjustedEmotion, confidence);
            
            // Process emotion with our improved handler
            processEmotion(adjustedEmotion, emojiUrl, properPercentageText);
          } else {
            // If no result, use a fallback
            processEmotion('neutral', emotionEmojiMap.neutral, formatEmotionText('neutral', 50));
          }
        } catch (error) {
          console.error("Error in emotion detection:", error);
          // Use fallback on error
          processEmotion('neutral', emotionEmojiMap.neutral, formatEmotionText('neutral', 50));
        }
      }, 100); // 100ms for smoother updates
    } else {
      // Simulated emotion detection (fallback)
      detectionIntervalRef.current = window.setInterval(() => {
        if (!detectionActive.current) return;
        
        // Cycle through emotions with a bias toward neutral (more realistic)
        const randomValue = Math.random();
        const emotions = ['happy', 'neutral', 'happy', 'surprised', 'sad', 'surprised']; // Higher chance of neutral
        const emotionIndex = Math.floor(randomValue * emotions.length);
        const emotion = emotions[emotionIndex];
        const emojiUrl = getEmojiForEmotion(emotion);
        
        // Generate a realistic confidence percentage between 30-95%
        const confidence = Math.floor(Math.random() * 65) + 30;
        const percentageText = formatEmotionText(emotion, confidence);
        
        // Track emotion frequencies
        if (emotion) {
          emotionFrequenciesRef.current[emotion] = (emotionFrequenciesRef.current[emotion] || 0) + 1;
          
          // Notify parent about updated emotion frequencies with stringified data
          if (onEmotionFrequencyUpdate) {
            onEmotionFrequencyUpdate(JSON.stringify(emotionFrequenciesRef.current));
          }
        }
        
        processEmotion(emotion, emojiUrl, percentageText);
      }, 500);  // Moderate updates for simulated emotions
    }
  };

  return null; // This is a non-visual component
};

export default EmotionDetector;
